import { ThemeProvider } from '@mui/material';
import { DatePicker, type DatePickerProps } from '@mui/x-date-pickers';
import { type Dayjs } from 'dayjs';
import useAppTheme from 'hooks/useAppTheme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { darkTheme, lightTheme } from './MuiDatePickerTheme';

interface Props extends DatePickerProps<Dayjs> {
  format?: string;
}

const CustomDatePicker = ({ format = 'DD-MM-YYYY', sx, ...props }: Props) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const datePickerSx = {
    '& .MuiInputBase-root': {
      backgroundColor: enableDarkTheme
        ? palette.toolBarBackground
        : palette.menuOptions,
      border: `1px solid ${enableDarkTheme ? palette.borderGrey : '#e0e0e0'}`,
      borderRadius: '8px',
      height: '32px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: enableDarkTheme ? palette.border : '#bdbdbd',
        backgroundColor: enableDarkTheme ? palette.boxBackground : '#f8f9fa',
      },
      '&.Mui-focused': {
        borderColor: palette.primary,
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.menuOptions,
        boxShadow: `0 0 0 2px ${palette.primary}4d`,
      },
      '&.Mui-disabled': {
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.sectionBackground,
        borderColor: enableDarkTheme ? palette.borderGrey : palette.border,
        opacity: 1,
      },
    },
    '& .MuiInputBase-input': {
      color: enableDarkTheme ? palette.textPrimary : palette.textPrimary,
      fontSize: '14px',
      padding: '8px 12px',
      fontWeight: 400,
      '&.Mui-disabled': {
        color: palette.textSecondary,
        WebkitTextFillColor: palette.textSecondary,
        opacity: 1,
      },
    },
    '& .MuiIconButton-root': {
      color: enableDarkTheme ? palette.lightGrey : 'rgba(0, 0, 0, 0.6)',
      padding: '4px',
      '&:hover': {
        backgroundColor: enableDarkTheme
          ? `${palette.primary}1a`
          : 'rgba(0, 0, 0, 0.04)',
      },
      '&.Mui-disabled': {
        color: palette.textSecondary,
        opacity: 1,
      },
    },
    '& .MuiDateCalendar-root': {
      color: palette.textPrimary,
      borderRadius: 2,
      borderWidth: 1,
      borderColor: palette.primary,
      border: '1px solid',
      backgroundColor: enableDarkTheme
        ? palette.modalBackground
        : palette.menuOptions,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    minWidth: '180px',
    ...sx,
  };

  return (
    <ThemeProvider theme={enableDarkTheme ? darkTheme : lightTheme}>
      <DatePicker sx={datePickerSx} {...props} format={format} />
    </ThemeProvider>
  );
};

export default CustomDatePicker;
